// Pure mathematical helpers shared across the engine.
// No I/O, no React, no data lookups — everything testable in isolation.

export const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha'];

// (score - 10) / 2, floored. Standard 5e formula.
export const abilityMod = (score) => {
  if (typeof score !== 'number' || Number.isNaN(score)) return 0;
  return Math.floor((score - 10) / 2);
};

// Proficiency bonus by character level (1-20).
// L1-4 = +2, L5-8 = +3, L9-12 = +4, L13-16 = +5, L17-20 = +6.
export const proficiencyBonus = (level) => {
  const lvl = Math.max(1, Math.min(20, Math.floor(level || 1)));
  return Math.ceil(lvl / 4) + 1;
};

// Format a modifier for display: +3 / -1 / +0.
export const formatMod = (mod) => (mod >= 0 ? `+${mod}` : `${mod}`);

// Max value of a hit die.
export const maxHitDie = (sides) => sides;

// HP at level 1 — max of class hit die + Con modifier.
export const hpAtLevel1 = (hitDie, conMod) => maxHitDie(hitDie) + conMod;

// HP for levels 2..N using the "take average" convention (DDB default; see
// srd/character-creation-plan.md Q5 → A with default Fixed).
// avgPerLevel = ceil(hitDie/2) + 1 + conMod
export const hpAverage = (hitDie, level, conMod) => {
  if (level < 1) return 0;
  const base = hpAtLevel1(hitDie, conMod);
  if (level === 1) return base;
  const perLevel = Math.ceil(hitDie / 2) + 1 + conMod;
  return base + (level - 1) * perLevel;
};

// Aggregate ability scores from sources, returning {str: total, dex: ..., ...}.
// Each source is `{kind: 'fixed', increases: {str: 2}}` or `{kind: 'choose', ...}`
// with the chosen allocations supplied externally. This helper applies
// fixed bonuses; "choose" sources should be resolved upstream to the
// chosen allocation map before being passed in here.
export const sumAbilityBonuses = (...maps) => {
  const result = { str: 0, dex: 0, con: 0, int: 0, wis: 0, cha: 0 };
  for (const m of maps) {
    if (!m) continue;
    for (const ab of ABILITIES) {
      if (typeof m[ab] === 'number') result[ab] += m[ab];
    }
  }
  return result;
};

// Compose base scores + bonus maps into final ability scores.
export const composeAbilities = (base, ...bonusMaps) => {
  const bonuses = sumAbilityBonuses(...bonusMaps);
  const result = {};
  for (const ab of ABILITIES) {
    result[ab] = (base?.[ab] || 0) + bonuses[ab];
  }
  return result;
};
